// language-config.ts
// Single source of truth for all language-related configuration

export interface LanguageConfig {
  /** The canonical language code used internally */
  code: string;
  /** URL path segment (lowercase, URL-safe) */
  urlSegment: string;
  /** Display name for UI */
  displayName: string;
  /** Native name for the language */
  nativeName: string;
  /** HTML lang attribute value */
  htmlLang: string;
  /** Text direction */
  dir: "ltr" | "rtl";
  /** Associated country codes for geolocation detection */
  countryCodes: string[];
}

/**
 * Master language configuration map
 * Key: canonical language code
 * Add new languages here - all other files will pick them up automatically
 */
export const LANGUAGE_CONFIG: Record<string, LanguageConfig> = {
  en: {
    code: "en",
    urlSegment: "en",
    displayName: "English",
    nativeName: "English",
    htmlLang: "en",
    dir: "ltr",
    countryCodes: ["GB", "AU", "NZ", "CA", "IN", "PH"],
  },
  th: {
    code: "th",
    urlSegment: "th",
    displayName: "Thai",
    nativeName: "ภาษาไทย",
    htmlLang: "th",
    dir: "ltr",
    countryCodes: ["TH"],
  },
  // Add new languages here:
  // ko: {
  //   code: "ko",
  //   urlSegment: "ko",
  //   displayName: "Korean",
  //   nativeName: "한국어",
  //   htmlLang: "ko",
  //   dir: "ltr",
  //   countryCodes: ["KR"],
  // },
};

/** Default language code */
export const DEFAULT_LANGUAGE = "en";

// ============================================================================
// Derived maps and utilities (auto-generated from LANGUAGE_CONFIG)
// ============================================================================

/** All available language codes */
export const AVAILABLE_LANGUAGES = Object.keys(LANGUAGE_CONFIG);

/** Map: URL segment (lowercase) -> canonical language code */
export const URL_TO_LANGUAGE_MAP: Record<string, string> = Object.values(
  LANGUAGE_CONFIG
).reduce(
  (acc, config) => {
    acc[config.urlSegment.toLowerCase()] = config.code;
    return acc;
  },
  {} as Record<string, string>
);

/** Map: country code -> canonical language code */
export const COUNTRY_TO_LANGUAGE_MAP: Record<string, string> = Object.values(
  LANGUAGE_CONFIG
).reduce(
  (acc, config) => {
    config.countryCodes.forEach((country) => {
      acc[country] = config.code;
    });
    return acc;
  },
  {} as Record<string, string>
);

/** Map: canonical language code -> HTML lang attribute */
export const LANGUAGE_TO_HTML_LANG_MAP: Record<string, string> = Object.values(
  LANGUAGE_CONFIG
).reduce(
  (acc, config) => {
    acc[config.code] = config.htmlLang;
    return acc;
  },
  {} as Record<string, string>
);

/** Set of valid URL segments for quick lookup */
export const VALID_URL_SEGMENTS = new Set(
  Object.values(LANGUAGE_CONFIG).map((c) => c.urlSegment.toLowerCase())
);

// ============================================================================
// Utility functions
// ============================================================================

/**
 * Get language config by code
 */
export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return LANGUAGE_CONFIG[code];
}

/**
 * Get language code from URL segment
 */
export function getLanguageFromUrlSegment(segment: string): string | null {
  return URL_TO_LANGUAGE_MAP[segment.toLowerCase()] ?? null;
}

/**
 * Get language code from country code
 */
export function getLanguageFromCountry(countryCode: string): string | null {
  return COUNTRY_TO_LANGUAGE_MAP[countryCode] ?? null;
}

/**
 * Get URL segment for a language code
 */
export function getUrlSegmentForLanguage(code: string): string {
  return LANGUAGE_CONFIG[code]?.urlSegment ?? LANGUAGE_CONFIG[DEFAULT_LANGUAGE].urlSegment;
}

/**
 * Check if a URL segment is a valid language
 */
export function isValidUrlSegment(segment: string): boolean {
  return VALID_URL_SEGMENTS.has(segment.toLowerCase());
}

/**
 * Get HTML lang attribute for a language code
 */
export function getHtmlLang(code: string): string {
  return LANGUAGE_TO_HTML_LANG_MAP[code] ?? "en";
}

/**
 * Get text direction for a language code
 */
export function getTextDirection(code: string): "ltr" | "rtl" {
  return LANGUAGE_CONFIG[code]?.dir ?? "ltr";
}
